import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Copy, Sheet } from "lucide-react";
import { toast } from "sonner";
import type {
  CutPiece,
  SpanWarning,
  SupportPlacement,
  TerminationPlacement,
} from "@/hooks/use-pipe-sketch";
import { formatFtIn, getMaterial, type MaterialId } from "@/lib/pipe-spec";

interface Totals {
  totalFeet: number;
  totalWeight: number;
  supportWeight: number;
  bySize: [string, { feet: number; weight: number; pieces: number }][];
  fittingCounts: [string, number][];
  componentCounts: [string, number][];
  supportCounts: [string, number][];
  terminationCounts: [string, number][];
  welds: number;
  pieces: number;
}

export default function PipeCutList({
  cutList,
  totals,
  material,
  name,
  supports = [],
  terminations = [],
  spanWarnings = [],
  selectedTag,
  onSelectTag,
}: {
  cutList: CutPiece[];
  totals: Totals;
  material: MaterialId;
  name?: string;
  supports?: SupportPlacement[];
  terminations?: TerminationPlacement[];
  spanWarnings?: SpanWarning[];
  selectedTag?: string | null;
  onSelectTag?: (tag: string | null) => void;
}) {
  const rowRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    if (!selectedTag) return;
    rowRefs.current[selectedTag]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedTag]);

  const mat = getMaterial(material);
  const jointWord = mat.jointType === "bw" ? "Field welds" : "Joints";

  const copy = () => {
    const lines = [
      `CUT LIST — ${name ?? "Pipe sketch"}`,
      `${mat.label} · ${mat.spec} · ${mat.joint}`,
      "",
      ...cutList.map(
        (p) =>
          `${p.tag}. ${p.size} — cut ${formatFtIn(p.cutLength)}  (C/C ${formatFtIn(
            p.centerLength,
          )}, ${p.weldEnds} joint end${p.weldEnds === 1 ? "" : "s"})`,
      ),
      "",
      "FITTINGS",
      ...totals.fittingCounts.map(([k, n]) => `${n} × ${k}`),
      ...(totals.componentCounts.length
        ? ["", "VALVES / SPECIALTIES", ...totals.componentCounts.map(([k, n]) => `${n} × ${k}`)]
        : []),
      ...(supports.length
        ? [
            "",
            "SUPPORT SCHEDULE",
            ...supports.map((s) => `${s.mark}  ${s.size} ${s.label} — ${s.rodSize || s.detail}`),
          ]
        : []),
      ...(terminations.length
        ? [
            "",
            "TERMINATIONS",
            ...terminations.map((t) => `${t.tag ?? t.mark}  ${t.size} ${t.label}`),
          ]
        : []),
      "",
      `Total pipe: ${totals.totalFeet.toFixed(2)} ft`,
      `Est. weight: ${totals.totalWeight.toFixed(1)} lb`,
      ...(totals.supportWeight
        ? [`Support hardware: ${totals.supportWeight.toFixed(1)} lb`]
        : []),
      `${jointWord}: ${totals.welds}`,
    ];
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Cut list copied");
  };
  const exportCsv = () => {
    const esc = (v: string | number) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const row = (...cells: (string | number)[]) => cells.map(esc).join(",");
    const rows: string[] = [
      row("Sketch", name ?? "Pipe sketch"),
      row("Material", `${mat.label} · ${mat.spec} · ${mat.joint}`),
      "",
      row("Section", "Mark", "Size", "Description", "Cut length (in)", "Cut length", "C/C (in)", "C/C", "Joint ends", "Qty"),
      ...cutList.map((p) =>
        row(
          "Cut list",
          p.tag,
          p.size,
          "Pipe",
          p.cutLength.toFixed(3),
          formatFtIn(p.cutLength),
          p.centerLength.toFixed(3),
          formatFtIn(p.centerLength),
          p.weldEnds,
          1,
        ),
      ),
      ...totals.fittingCounts.map(([k, n]) =>
        row("Fittings", "", "", k, "", "", "", "", "", n),
      ),
      ...totals.componentCounts.map(([k, n]) =>
        row("Valves & specialties", "", "", k, "", "", "", "", "", n),
      ),
      ...supports.map((s) =>
        row(
          "Supports",
          s.mark,
          s.size,
          `${s.label}${s.rodSize ? ` — ${s.rodSize}` : s.detail ? ` — ${s.detail}` : ""}`,
          "",
          "",
          "",
          "",
          "",
          1,
        ),
      ),
      ...terminations.map((t) =>
        row("Terminations", t.mark, t.size, t.label, "", "", "", "", t.joints ?? "", 1),
      ),
      ...totals.bySize.map(([sz, v]) =>
        row("Totals by size", "", sz, "Pipe", "", `${v.feet.toFixed(2)} ft`, "", "", "", v.pieces),
      ),
      "",
      row("Total pipe (ft)", totals.totalFeet.toFixed(2)),
      row("Est. pipe weight (lb)", totals.totalWeight.toFixed(1)),
      ...(totals.supportWeight
        ? [row("Support hardware (lb)", totals.supportWeight.toFixed(1))]
        : []),
      row(jointWord, totals.welds),
    ];

    const blob = new Blob([`\uFEFF${rows.join("\r\n")}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(name ?? "pipe-sketch").replace(/[^\w-]+/g, "-").toLowerCase()}-cut-list.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Cut List</h2>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={copy} disabled={!cutList.length}>
            <Copy className="mr-1 h-3.5 w-3.5" /> Copy
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={exportCsv}
            disabled={!cutList.length}
            title="Export cut list and schedules as CSV"
          >
            <Sheet className="mr-1 h-3.5 w-3.5" /> CSV
          </Button>
        </div>
      </div>


      <p className="text-[11px] text-muted-foreground">
        {mat.label} · {mat.fittingSpec}. Cut lengths are center-to-center minus fitting take-outs
        and the face-to-face of any valve or flange set placed on the line.
      </p>

      {!cutList.length && (
        <p className="text-xs text-muted-foreground">
          Click the grid to set a start point, then click along an axis to run pipe. Turns get an
          elbow, size changes get a reducer, Branch mode tees off an existing run, and Place mode
          drops valves or flanges in line.
        </p>
      )}


      {cutList.length > 0 && (
        <div className="space-y-1">
          <p className="text-[11px] text-muted-foreground">
            Tap a piece to highlight it on the ISO.
          </p>
          {cutList.map((p) => {
            const active = selectedTag === p.tag;
            return (
              <button
                type="button"
                key={`${p.runId}-${p.index}-${p.sub}`}
                ref={(el) => {
                  rowRefs.current[p.tag] = el;
                }}
                aria-pressed={active}
                onClick={() => onSelectTag?.(active ? null : p.tag)}
                className={`flex w-full items-center justify-between rounded-md border px-2 py-1 text-left text-xs transition-colors ${
                  active
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border hover:bg-muted/50"
                }`}
              >
                <span className={`font-mono ${active ? "text-primary" : "text-muted-foreground"}`}>
                  {p.tag}
                </span>
                <span className="font-medium">{p.size}</span>
                <span className="font-mono">{formatFtIn(p.cutLength)}</span>
                <span className="font-mono text-[10px] text-muted-foreground">
                  C/C {formatFtIn(p.centerLength)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {totals.fittingCounts.length > 0 && (
        <div className="space-y-1">
          <h3 className="text-xs font-semibold">Fittings</h3>
          {totals.fittingCounts.map(([k, n]) => (
            <div key={k} className="flex justify-between text-xs">
              <span>{k}</span>
              <span className="font-mono">{n}</span>
            </div>
          ))}
        </div>
      )}

      {totals.componentCounts.length > 0 && (
        <div className="space-y-1">
          <h3 className="text-xs font-semibold">Valves &amp; specialties</h3>
          {totals.componentCounts.map(([k, n]) => (
            <div key={k} className="flex justify-between text-xs">
              <span>{k}</span>
              <span className="font-mono">{n}</span>
            </div>
          ))}
        </div>
      )}

      {spanWarnings.length > 0 && (
        <div className="space-y-1 rounded-md border border-destructive/40 bg-destructive/10 p-2">
          <h3 className="flex items-center gap-1 text-xs font-semibold text-destructive">
            <AlertTriangle className="h-3.5 w-3.5" /> Support spacing
          </h3>
          {spanWarnings.map((w, i) => (
            <p key={`${w.runId}-${w.index}-${i}`} className="text-[11px] text-destructive">
              {w.size} run spans {w.spanFt.toFixed(1)} ft — MSS SP-58 max {w.maxFt.toFixed(1)} ft.
              Add {w.needed} support{w.needed === 1 ? "" : "s"}.
            </p>
          ))}
        </div>
      )}

      {supports.length > 0 && (
        <div className="space-y-1">
          <h3 className="text-xs font-semibold">Support schedule</h3>
          {supports.map((sp) => (
            <div key={sp.mark} className="flex justify-between gap-2 text-xs">
              <span className="font-mono text-muted-foreground">{sp.mark}</span>
              <span className="flex-1">{sp.label}</span>
              <span className="font-mono text-[10px] text-muted-foreground">
                {sp.rodSize || sp.detail}
              </span>
            </div>
          ))}
        </div>
      )}

      {terminations.length > 0 && (
        <div className="space-y-1">
          <h3 className="text-xs font-semibold">Terminations</h3>
          {terminations.map((t) => (
            <div key={t.mark} className="flex justify-between gap-2 text-xs">
              <span className="font-mono text-muted-foreground">{t.mark}</span>
              <span className="flex-1">{t.label}</span>
              <span className="font-mono text-[10px] text-muted-foreground">{t.size}</span>
            </div>
          ))}
        </div>
      )}

      {cutList.length > 0 && (
        <div className="space-y-1 border-t border-border pt-2 text-xs">
          {totals.bySize.map(([sz, v]) => (
            <div key={sz} className="flex justify-between text-muted-foreground">
              <span>{sz} pipe</span>
              <span className="font-mono">
                {v.feet.toFixed(2)} ft · {v.weight.toFixed(0)} lb
              </span>
            </div>
          ))}
          <div className="flex justify-between font-semibold">
            <span>Total pipe</span>
            <span className="font-mono">{totals.totalFeet.toFixed(2)} ft</span>
          </div>
          <div className="flex justify-between font-semibold">
            <span>Est. weight</span>
            <span className="font-mono">{totals.totalWeight.toFixed(1)} lb</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>{jointWord}</span>
            <span className="font-mono">{totals.welds}</span>
          </div>
        </div>
      )}
    </div>
  );
}
